import { HistoryChangeType } from 'gql/graphql';
import { Badge } from 'uikit';

import cn from 'classnames';
import type { ComponentProps } from 'react';
import { useCallback, useState } from 'react';

import Styles from './historyBadgeFilter.module.css';

const commonBadgeProps: ComponentProps<typeof Badge> = {
    as: 'button',
    view: 'stroked',
    form: 'round',
};

interface IBadgeFilterProps<T> {
    handlerBadgeFilterChange: ({ filterType, value }: { filterType: 'typeFilter'; value: T | null }) => void;
    countsObj: { deletedCostsCount: number; updatedCostsCount: number; createdCostsCount: number };
}

export const HistoryBadgeFilter = <T,>({ handlerBadgeFilterChange, countsObj }: IBadgeFilterProps<T>) => {
    const [activeBadge, setActiveBadge] = useState<T | null>(null);

    const onBadgeClick = useCallback(
        (badgeType: T) => () => {
            setActiveBadge((prev) => {
                if (prev === badgeType) {
                    handlerBadgeFilterChange({
                        value: null,
                        filterType: 'typeFilter',
                    });

                    return null;
                }

                handlerBadgeFilterChange({
                    value: badgeType,
                    filterType: 'typeFilter',
                });

                return badgeType;
            });
        },
        [handlerBadgeFilterChange],
    );

    return (
        <div>
            <Badge
                status="success"
                label={`${countsObj.createdCostsCount} новых`}
                onClick={onBadgeClick(HistoryChangeType.New as T)}
                className={cn(Styles.badge, Styles.badgeNew, {
                    [Styles.inactiveBadge]: activeBadge && activeBadge !== HistoryChangeType.New,
                })}
                {...commonBadgeProps}
            />
            <Badge
                status="error"
                label={`${countsObj.deletedCostsCount} удалены`}
                onClick={onBadgeClick(HistoryChangeType.Deleted as T)}
                className={cn(Styles.badge, Styles.badgeDeleted, {
                    [Styles.inactiveBadge]: activeBadge && activeBadge !== HistoryChangeType.Deleted,
                })}
                {...commonBadgeProps}
            />
            <Badge
                status="warning"
                label={`${countsObj.updatedCostsCount} изменены`}
                className={cn(Styles.badge, Styles.badgeUpdated, {
                    [Styles.inactiveBadge]: activeBadge && activeBadge !== HistoryChangeType.Updated,
                })}
                onClick={onBadgeClick(HistoryChangeType.Updated as T)}
                {...commonBadgeProps}
            />
        </div>
    );
};

HistoryBadgeFilter.displayName = 'HistoryBadgeFilter';
